import { useEffect, useRef, useState } from "react";
import { useChatComposerDraft } from "../../hooks/useChatComposerDraft";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";
import { Resizable } from "re-resizable";
import useEventCallback from "../../hooks/useEventCallback";
import {
  BookOpen,
  ArrowLeft,
  Circle,
  CircleCheckBig,
  Calendar,
  Clock,
  ExternalLink,
  DoorOpen,
  Loader2,
  Trash2,
  Archive,
  ChevronDown,
  ChevronUp
} from "lucide-react";

import { getTaskThreadService } from "../../utils/chatService";
import { updateTaskService, deleteTaskFileService, getTaskByIdService } from "../../utils/taskService";
import { deleteEmailActionByTypeService, deleteEmailFileIdService } from "../../utils/digestService";
import { getEmailWebLinkService } from "../../utils/emailService";
import { resolveOriginalMessageLink } from "../../utils/mailProvider";
import {
  createTaskThread,
  replaceTaskFileIds,
  deleteEmailFileId
} from "../../reducers/chatReducer";
import { saveTask, updateTask, addTasks } from "../../reducers/taskReducer";
import { formatDate, formatTaskDate, formatEstimation } from "../../utils/formatter";
import { handleChatMessage, readChatSSEFrames } from "../../utils/handleChatMessages";
import {
  CHAT_UNAVAILABLE_CODE,
  isChatUnavailableError,
  isChatUnavailablePayload,
  responseToChatUnavailableError,
} from "../../utils/chatAvailability";
import { clearAssistantUnavailable, setAssistantUnavailable } from "../../reducers/assistantReducer";
import { applyTaskChatFrame, finalizeTaskChatOpening } from "./taskChatStream";
import PrinceGeoffrey from "../Chat/PrinceGeoffrey";
import Messages from "../Chat/Messages";
import ChatBox from "../Chat/ChatBox";
import TaskSetup from "./TaskSetup";
import TaskScheduler from "./TaskScheduler";
import MessagesContainer from "../Chat/MessagesContainer";
import MessagesThinkingFeed from "../Chat/MessageThinkingFeed";
import { readFileAsBase64 } from "../../utils/fileUtils";
import TaskActionRibbon from "../Digest/TaskActionRibbon";
import TaskEmailLink from "./TaskEmailLink";
import { emailSourceKey } from "./taskEmailProvenance";

export { emailSourceKey } from "./taskEmailProvenance";

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function Task() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getToken, userId } = useAuth();
  const getThreadsCalled = useRef(false);
  const taskIsStarting = useRef(false);
  const taskIsRestarting = useRef(false);
  const prevTaskRef = useRef(null);
  const messagesEndRef = useRef(null);
  const params = useParams();

  const [isTaskCompleted, setIsTaskCompleted] = useState(false);
  const [threadIsLoaded, setThreadIsLoaded] = useState(false);
  const {
    resetChatBoxInput,
    setResetChatBoxInput,
    restoredChatBoxInput,
    setRestoredChatBoxInput,
    clearComposer,
    restoreComposerDraft,
  } = useChatComposerDraft();
  const [chatMessages, setChatMessages] = useState([]);
  const [messageLoading, setMessageLoading] = useState(false);
  const [thinkingFeed, setThinkingFeed] = useState([]);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [isTaskNotesOpen, setIsTaskNotesOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const [isTaskDetailsOpen, setIsTaskDetailsOpen] = useState(true);

  const assistant = useSelector((state) => state.assistant);

  // Resizable container - temporary location
  // TODO: This is a temporary solution. Need to move this to shareable component
  const [containerWidth, setContainerWidth] = useState(0);
  const [leftWidth, setLeftWidth] = useState(0);
  const containerRef = useRef(null);
  const MIN_LEFT_WIDTH = 300; // Minimum width for left panel in pixels
  const MIN_LEFT_PERCENTAGE = 0.25; // Minimum percentage of total width
  const DEFAULT_LEFT_RATIO = .4; // Default left panel takes 2/5 of the screen

  const currentTask = useSelector((state) =>
    params.id
      ? state.taskList?.tasks?.find((task) => task.id === params.id)
      : state.taskList?.currentTask
  );

  const currentProject = useSelector((state) =>
    currentTask?.metaData?.projectId
      ? state.projectList?.projects?.find(
          (project) => project.id === currentTask.metaData.projectId
        )
      : null
  );

  const projectChat = useSelector((state) => {
    const sortedChats = [...state.chats].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return sortedChats.find((chat) => chat.projectId && chat.projectId === currentProject?.id && !chat.taskId)
  });

  const taskChat = useSelector((state) => {
    const sortedChats = [...state.chats].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return sortedChats.find((chat) => {
      return chat.taskId && currentTask?.id && chat.taskId === currentTask?.id
    });
  });

  const [emailWebLinks, setEmailWebLinks] = useState({});
  const requestedEmailIdsRef = useRef(new Set());

  useEffect(() => {
    const sources = [
      currentTask?.metaData?.emailId
        ? {
            emailId: currentTask.metaData.emailId,
            mailIntegrationId: currentTask.metaData.mailIntegrationId,
          }
        : null,
      ...(projectChat?.emailFileIds ?? []).map(({ emailId, mailIntegrationId }) => ({
        emailId,
        mailIntegrationId,
      })),
    ].filter(({ emailId }) => Boolean(emailId));
    const pendingSources = sources.filter((source) => (
      !requestedEmailIdsRef.current.has(emailSourceKey(source))
    ));

    if (pendingSources.length === 0) return undefined;

    pendingSources.forEach((source) => requestedEmailIdsRef.current.add(emailSourceKey(source)));

    let cancelled = false;

    (async () => {
      for (const source of pendingSources) {
        let link = null;
        try {
          link = await getEmailWebLinkService(getToken, source);
        } catch (error) {
          link = null;
        }
        if (cancelled) return;
        setEmailWebLinks((previous) => ({
          ...previous,
          [emailSourceKey(source)]: link ?? null,
        }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    currentTask?.metaData?.emailId,
    currentTask?.metaData?.mailIntegrationId,
    projectChat?.emailFileIds,
    getToken,
  ]);

  const getIsMobileOnly = () => {
    return windowWidth < 768
  }

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }
    
    window.addEventListener('resize', handleResize)
    handleResize() // Initialize on mount
    
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages]);

  // Reset states when task changes, but only if it's a new task (not just a change to "completed") 
  useEffect(() => { 
    if (prevTaskRef.current && prevTaskRef.current.id === currentTask?.id) { 
      // Only the completed flag has changed, so update it without resetting the chat state 
      setIsTaskCompleted(currentTask?.completed); 
    } else { 
      // Task has changed meaningfully, so reset all the state 
      setThreadIsLoaded(false); 
      getThreadsCalled.current = false; 
      setChatMessages([]); 
      setMessageLoading(false); 
      setResetChatBoxInput(false); 
      setIsTaskCompleted(currentTask?.completed); 
    } // Update the ref with the current task for the next comparison 
    prevTaskRef.current = currentTask; 
  }, [currentTask]);

  useEffect(() => {
    const getTaskDetails = async () => {
      // Go to the server, get the Task information, and update the task in the redux store
      const response = await getTaskByIdService(params.id);

      dispatch(addTasks({tasks: [response]}));
    }

    if (!currentTask?.id) {
      getTaskDetails();
    }
  }, [currentTask?.id, dispatch, params.id]);

  const initializeTaskThread = useEventCallback(async () => {
    if (
      !threadIsLoaded &&
      !getThreadsCalled.current &&
      currentTask?.id &&
      !taskIsRestarting.current
    ) {
      try {
        if (
          !taskChat?.id &&
          !currentTask?.archived &&
          currentTask?.status &&
          !["pending", "ready", "completed", "on-hold", "cancelled"].includes(
            currentTask?.status
          )
        ) {
          await getOrCreateTaskThread();
        } else if (taskChat?.id) {
          await fetchMessagesByChatId(taskChat.id);
        }
      } catch (error) {
        console.error("Error initializing thread:", error);
      }
    }
  });

  useEffect(() => {
    initializeTaskThread();

    // Cleanup only when component unmounts
    return () => {
      getThreadsCalled.current = false;
    };
  }, [
    currentTask?.archived,
    currentTask?.id,
    currentTask?.status,
    initializeTaskThread,
    taskChat?.id,
    threadIsLoaded,
  ]);

  // Resizable container - effects and handlers
  // TODO: This is a temporary solution. Need to move this to shareable component
  useEffect(() => {
    // Initialize container width and calculate initial left width

    const updateDimensions = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setContainerWidth(width);
        setLeftWidth(width * DEFAULT_LEFT_RATIO);
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  // Handle resize events
  // TODO: This is a temporary solution. Need to move this to shareable component
  const handleResizeStop = (e, direction, ref, d) => {
    const newWidth = leftWidth + d.width;
    
    // Apply minimum width constraints
    if (newWidth >= MIN_LEFT_WIDTH && newWidth >= containerWidth * MIN_LEFT_PERCENTAGE) {
      setLeftWidth(newWidth);
    }
  };

  const toggleTaskDetails = () => {
    setIsTaskDetailsOpen(!isTaskDetailsOpen);
  }

  const getOrCreateTaskThread = async () => {
    if (getThreadsCalled.current) return;
    getThreadsCalled.current = true;
    taskIsStarting.current = true;
    setThreadIsLoaded(false);
    setMessageLoading(true);

    try {
      const token = await getToken();

      const params = new URLSearchParams({
        assistantId: assistant.id,
        vectorStoreId: projectChat?.vectorStoreId || "",
        chatId: taskChat?.id || "",
        taskId: currentTask?.id,
      });

      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL
        }/api/task/get_task_thread?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw await responseToChatUnavailableError(response);
      }

      let openingSucceeded = false;
      let openingFailed = false;
      await readChatSSEFrames(response, (data) => {
        if (isChatUnavailablePayload(data)) openingFailed = true;
        openingSucceeded = applyTaskChatFrame({ data, dispatch, setChatMessages, createTaskThread, projectId: currentProject?.id, taskId: currentTask.id }) || openingSucceeded;
      });

      await finalizeTaskChatOpening({
        openingSucceeded,
        openingFailed,
        dispatch,
        persistTaskFile: () => addOrUpdateTaskFile(isTaskCompleted),
      });
    } catch (error) {
      if (isChatUnavailableError(error)) {
        dispatch(setAssistantUnavailable({ reason: error?.response?.data?.error?.code || CHAT_UNAVAILABLE_CODE }));
      }
      console.error("Error creating task thread:", error);
    } finally {
      setMessageLoading(false);
      setThreadIsLoaded(true);
      getThreadsCalled.current = false;
      taskIsStarting.current = false;
    }
  };

  const handleRestartTaskThread = async (e) => {
    e.stopPropagation();

    // Clear messages and set loading states immediately
    setChatMessages([]);
    setThreadIsLoaded(false);
    setMessageLoading(true);

    if (!getThreadsCalled.current) {
      getThreadsCalled.current = true;
      taskIsRestarting.current = true;

      try {
        const token = await getToken();

        const params = new URLSearchParams({
          assistantId: assistant.id,
          vectorStoreId: projectChat?.vectorStoreId || "",
          taskId: currentTask?.id,
        });

        const response = await fetch(
          `${API_BASE_URL}/api/task/restart_task_thread?${params.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw await responseToChatUnavailableError(response);
        }

        let openingSucceeded = false;
        let openingFailed = false;
        await readChatSSEFrames(response, (data) => {
          if (isChatUnavailablePayload(data)) openingFailed = true;
          openingSucceeded = applyTaskChatFrame({ data, dispatch, setChatMessages, createTaskThread, projectId: currentProject?.id, taskId: currentTask.id }) || openingSucceeded;
        });

        await finalizeTaskChatOpening({
          openingSucceeded,
          openingFailed,
          dispatch,
          persistTaskFile: () => addOrUpdateTaskFile(isTaskCompleted),
        });
      } catch (error) {
        if (isChatUnavailableError(error)) {
          dispatch(setAssistantUnavailable({ reason: error?.response?.data?.error?.code || CHAT_UNAVAILABLE_CODE }));
        }
        console.error("Error restarting task thread:", error);
      } finally {
        setMessageLoading(false);
        setThreadIsLoaded(true);
        getThreadsCalled.current = false;
        taskIsRestarting.current = false;
      }
    }
  };

  const fetchMessagesByChatId = async (chatId) => {
    if (getThreadsCalled.current) return;
    getThreadsCalled.current = true;
    setThreadIsLoaded(false);
    setMessageLoading(true);

    try {
      const response = await getTaskThreadService(getToken, { chatId });
      setChatMessages(response.data.messages);
    } catch (error) {
      console.error("Error fetching messages by chatId:", error);
    } finally {
      setMessageLoading(false);
      setThreadIsLoaded(true);
      getThreadsCalled.current = false;
    }
  };

  const handleSendMessage = async (message, file) => {
    if (!message.trim() || !taskChat) return;

    // Add user's message to the chat
    setChatMessages((prevMessages) => [
      ...prevMessages,
      {
        role: "user",
        content: [{ text: { value: message } }],
        metadata: file
          ? { file_name: file?.name, file_type: file?.type }
          : {},
      },
    ]);
    setMessageLoading(true);
    // Clear on send: the turn is away, and a second Enter must not re-send it.
    clearComposer();
    setThinkingFeed([]);
    
    try {
      const token = await getToken();
      const base64File = file ? await readFileAsBase64(file) : null;

      // Send the message and update the task in parallel
      const [messageResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/assistant/send_message`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            message,
            assistantId: assistant.id,
            chatId: taskChat.id,
            vectorStoreId: projectChat?.vectorStoreId || "",
            file: file 
              ? {
                  base64: base64File,
                  name: file?.name,
                  type: file?.type
                } 
              : null,
          }),
        }),
        // Quietly update the task in the background
        updateTaskService(getToken, userId, {
          id: currentTask.id,
          updated_at: new Date().toISOString()
        }).then(updatedTask => {
          dispatch(updateTask(updatedTask));
        })
      ]);

      if (!messageResponse.ok) {
        throw await responseToChatUnavailableError(messageResponse);
      }

      // Placeholder for the assistant's message response
      const newAssistantMessage = {
        role: "assistant",
        content: [{ text: { value: "" } }],
        chat_id: taskChat.id
      };
      setChatMessages((prevMessages) => [...prevMessages, newAssistantMessage]);
      // The server has already advanced the handle before this terminal stream;
      // no browser persistence or timeout sequencing remains.
      const terminalPayload = await handleChatMessage(messageResponse, setThinkingFeed, setChatMessages);
      if (isChatUnavailablePayload(terminalPayload)) {
        dispatch(setAssistantUnavailable({ reason: terminalPayload.error.code }));
        restoreComposerDraft(message, file);
      } else if (terminalPayload?.type === "chat_complete") {
        dispatch(clearAssistantUnavailable());
      } else {
        // A stream that ended without chat_complete is a failed turn too --
        // leaving the box cleared would lose what the user typed.
        restoreComposerDraft(message, file);
      }
    } catch (error) {
      if (isChatUnavailableError(error)) {
        dispatch(setAssistantUnavailable({ reason: error?.response?.data?.error?.code || CHAT_UNAVAILABLE_CODE }));
      }
      console.error("Error getting AI response:", error);
      restoreComposerDraft(message, file);

      // Fallback error message for the assistant
      setChatMessages((prevMessages) => [
        ...prevMessages,
        {
          role: "assistant",
          content: [
            {
              text: {
                value: "Sorry, there was an error processing your request",
              },
            },
          ],
        },
      ]);
    } finally {
      setMessageLoading(false);
      setThinkingFeed([]);
    }
  };

  const addOrUpdateTaskFile = async (updatedCompletedStatus) => {
    try {
      const token = await getToken();

      const taskStatus = updatedCompletedStatus ? "complete" : "in-progress";
      const taskData = {
        ...currentTask,
        status: taskStatus,
        messages: chatMessages,
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/task/upload_task_file`,
        {
          taskJson: taskData,
          taskId: currentTask.id,
          vectorStoreId: projectChat?.vectorStoreId || null,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      dispatch(
        replaceTaskFileIds({
          taskChatId: taskChat?.id,
          newFileId: response.data.newFileId,
        })
      );
    } catch (error) {
      console.error("Error updating project file:", error);
    }
  };

  const handleMarkDoneClick = async (e) => {
    e.stopPropagation();

    const updatedCompletedStatus = !isTaskCompleted;
    const updatedTask = await updateTaskService(getToken, userId, {
      ...currentTask,
      completed: updatedCompletedStatus,
    });
    await new Promise((resolve) => {
      dispatch(saveTask({ task: updatedTask }));
      // Give Redux a chance to update the state
      setTimeout(resolve, 0);
    });
    setIsTaskCompleted(updatedCompletedStatus);
    addOrUpdateTaskFile(updatedCompletedStatus);
  };

  const handleArchiveTask = async (e) => {
    e.stopPropagation();

    try {
      const updatedTask = await updateTaskService(getToken, userId, {
        ...currentTask,
        archived: true
      });
      dispatch(saveTask({ task: updatedTask }));
    } catch (error) {
      console.error('Error updating task in component:', error);
    }
  };

  const handleReschedule = async (newStartDate, newEndDate) => {
    const updatedTask = await updateTaskService(getToken, userId, {
      ...currentTask,
      startBy: newStartDate,
      endBy: newEndDate
    });
    dispatch(saveTask({ task: updatedTask }));
    setIsRescheduling(false);
  };

  const handleDeleteEmailFileFromProject = async (emailSource, fileId) => {
    if (projectChat?.id) {
      await deleteEmailFileIdService(getToken, { ...emailSource, fileId });
      await deleteTaskFileService(getToken, { fileId });
      await deleteEmailActionByTypeService(getToken, {
        ...emailSource,
        emailActionType: 'add-to-project',
      });

      dispatch(
        deleteEmailFileId({
          chatId: projectChat?.id,
          fileId: fileId,
        })
      );
    }
  };

  const handleExitTask = () => {
    if (currentTask?.metaData?.projectId) {
      navigate(`/project/${currentTask?.metaData?.projectId}`);
    } else {
      navigate(-1);
    }
  };

  const getTaskFiles = () => {
    return (
      <div className="flex flex-col">
        
        {currentTask?.metaData?.emailId && (
          <div className="mb-3">
            <h3 className="text-sm font-semibold">Task Created from Email</h3>
            <p className="text-sm">{currentTask?.metaData?.emailFrom}</p>
            <p className="text-sm">
              <TaskEmailLink
                link={emailWebLinks[emailSourceKey(currentTask?.metaData)]}
                className="flex items-center gap-2"
              >
                {currentTask?.metaData?.emailSubject} <ExternalLink size={16} />
              </TaskEmailLink>
            </p>
            {!resolveOriginalMessageLink(emailWebLinks[emailSourceKey(currentTask?.metaData)]).isAvailable && (
              <p className="text-xs text-stone-500">
                {resolveOriginalMessageLink(emailWebLinks[emailSourceKey(currentTask?.metaData)]).unavailableText}
              </p>
            )}
          </div>
        )}

        {projectChat?.vectorStoreId && (
          <div className="mb-3">
            <h3 className="text-sm font-semibold">Vector Store</h3>
            <p className="text-sm truncate">{projectChat?.vectorStoreId}</p>
          </div>
        )}

        {projectChat?.fileId && (
          <div className="mb-3">
            <h3 className="text-sm font-semibold">Project File</h3>
            <p className="text-sm truncate">{projectChat?.fileId}</p>
          </div>
        )}

        {taskChat?.fileId && (
          <div className="mb-3">
            <h3 className="text-sm font-semibold">Task File</h3>
            <p className="text-sm truncate">{taskChat?.fileId}</p>
          </div>
        )}

        {projectChat?.emailFileIds?.length > 0 && (
          <div className="mb-3">
            <h3 className="text-sm font-semibold">Email Files</h3>
            <div className="text-sm truncate">
              {projectChat?.emailFileIds?.map((file) => {
                const source = {
                  emailId: file.emailId,
                  mailIntegrationId: file.mailIntegrationId,
                };
                return (
                  <p key={`${emailSourceKey(source)}:${file.fileId}`} className="flex items-center gap-3">
                    <span>{file.fileId}</span>
                    <TaskEmailLink link={emailWebLinks[emailSourceKey(source)]}>
                      <ExternalLink size={16} />
                    </TaskEmailLink>
                    <button 
                      className="bg-transparent p-0 m-0 hover:bg-transparent text-red-500 hover:text-red-700"
                      onClick={() => handleDeleteEmailFileFromProject(source, file.fileId)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </p>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const TaskLoadingState = () => (
    <div className="flex flex-col items-center justify-center h-full w-full p-8">
      <Loader2 className="h-8 w-8 animate-spin text-gray-500 mb-4" />
      <p className="text-gray-600 text-lg">
        Working on your task, please standby...
      </p>
    </div>
  );

  const getTaskControls = () => {
    return (
      <>
        {taskChat?.id ? (
          <>
            {!isTaskCompleted && (
              <button
                className="ml-0 text-sm px-3 py-2"
                onClick={handleRestartTaskThread}
                data-amp-track-section="Task Workspace"
                data-amp-track-action="Restart_Task_Click"
                data-amp-track-description="Restart Task"
              >
                Restart Task
              </button>
            )}
            <button
              className="rounded-md px-2 ml-0 py-1 text-sm border-solid border border-stone-200 bg-transparent text-stone-700 hover:bg-stone-600 hover:text-white"
              onClick={handleMarkDoneClick}
              data-amp-track-section="Task Workspace"
              data-amp-track-action="Mark_Done_Click"
              data-amp-track-description={`Mark ${isTaskCompleted ? 'Mark Undone' : 'Mark Done'}`}
            >
              <div className="flex gap-2 items-center">
                {isTaskCompleted ? (
                  <>
                    <CircleCheckBig size={24} color="#34d399" /> Mark Undone
                  </>
                ) : (
                  <>
                    <Circle size={24} /> Mark Done
                  </>
                )}
              </div>
            </button>
          </>
        ) : (
          <>
            {!isTaskCompleted && (
              <button
                className="rounded-md px-3 ml-0 py-1 text-sm bg-gray-100 text-gray-600 hover:bg-gray-200"
                onClick={handleMarkDoneClick}
                data-amp-track-section="Task Workspace"
                data-amp-track-action="Mark_Done_Click"
                data-amp-track-description="Mark Done"
              >
                <div className="flex gap-1 items-center">
                  <Circle size={16} /> Mark Done
                </div>
              </button>
            )}
          </>
        )}
        {!currentTask?.metaData?.projectId && (
          <button
            className="rounded-md px-3 ml-0 py-1 text-sm bg-gray-100 text-gray-600 hover:bg-gray-200"
            onClick={handleArchiveTask}
            data-amp-track-section="Task Workspace"
            data-amp-track-action="Archive_Task_Click"
            data-amp-track-description="Archive Task"
          >
            <div className="flex gap-1 items-center">
              <Archive size={16} /> Archive
            </div>
          </button>
        )}
      </>
    );
  };

  const LatestActionSummary = () => {
    const latestAction = currentTask?.actions
      ? [...currentTask.actions].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 1)[0]
      : null;

    return (
      <div>
        <h3 className="text-base font-semibold text-gray-600 pb-2">Current Status</h3>
        <div className="text-base text-gray-600 pb-2">{latestAction?.summary}</div>
        <TaskActionRibbon item={currentTask} />
      </div>
    );
  };

  const RenderTaskDetails = () => {
    return (
      <>
        <div className="flex flex-col w-full text-left py-2 border-b border-stone-200">
          <h1 className="text-2xl leading-tight font-semibold">{currentTask?.title}</h1>
        </div>

        <div className="flex flex-col w-full h-fit">
          <section className="flex items-center gap-2">
            <div className={`flex flex-col w-full ${getIsMobileOnly() ? 'max-h-[55vh]' : 'max-h-[93vh]'}`}>
              <div
                className={`flex justify-between w-full items-center pt-3 ${
                  messageLoading ? "animate-pulse" : ""
                }`}
              >
                <PrinceGeoffrey size={getIsMobileOnly() ? 70 : 120} />
                {getIsMobileOnly() && (
                  <button
                    onClick={() => toggleTaskDetails()}
                    data-amp-track-section="Task Overview"
                    data-amp-track-action="Toggle_Task_Overview_Click"
                    data-amp-track-description="Toggle Mobile Task Overview"
                    className="text-sm m-0 p-0 text-stone-700 font-semibold bg-transparent hover:bg-transparent"
                  >
                    {isTaskDetailsOpen ? <ChevronDown size={28} /> : <ChevronUp size={28} />}
                  </button>
                )}
              </div>

              {isTaskDetailsOpen && (
                <>
                  {!taskChat?.id && !taskIsStarting.current ? (
                    <TaskSetup
                      task={currentTask}
                      onStart={getOrCreateTaskThread}
                    />
                  ) : (
                    <MessagesContainer messageLoading={messageLoading}>
                      {(taskChat?.id || taskIsStarting.current) &&
                        (!threadIsLoaded || chatMessages.length === 0) && (
                          <TaskLoadingState />
                      )}

                      {threadIsLoaded &&
                      chatMessages.length > 0 &&
                      !getThreadsCalled.current && (
                        <div className="flex flex-col relative text-base">
                          {chatMessages?.map((msg, index) => (
                            <Messages
                              key={index}
                              id={msg?.id}
                              chatId={taskChat?.id}
                              content={msg?.content[0]?.text?.value}
                              sender={msg?.role}
                              file_name={msg?.metadata?.file_name}                           
                            />
                          ))}
                          <MessagesThinkingFeed messageLoading={messageLoading} thinkingFeed={thinkingFeed} />
                          <div ref={messagesEndRef} />
                        </div>
                      )}
                    </MessagesContainer>
                  )}
                </>
              )}
            </div>
          </section>

          {isTaskDetailsOpen && threadIsLoaded && (
            <div className="relative">
              <ChatBox
                onSendMessage={handleSendMessage}
                setResetChatBoxInput={setResetChatBoxInput}
                showChatContainer={!messageLoading}
                resetChatBoxInput={resetChatBoxInput}
                restoredChatBoxInput={restoredChatBoxInput}
                setRestoredChatBoxInput={setRestoredChatBoxInput}
                ampSection="Task Overview"
              />
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div ref={containerRef}
      className={`w-full flex ${getIsMobileOnly() ? 'flex-col' : 'flex-row'}`}
    >
      {getIsMobileOnly() ? (
        <div className={`overflow-y-auto px-4 ${isTaskDetailsOpen ? 'h-3/5' : 'h-fit'}`}>
          {/* Left side content */}
          <div className="flex flex-col justify-center items-center h-fit">
            {RenderTaskDetails()}
          </div>
        </div>
      ) : (
        <Resizable
          size={{ width: leftWidth || containerWidth * DEFAULT_LEFT_RATIO, height: '100%' }}
          minWidth={Math.max(MIN_LEFT_WIDTH, containerWidth * MIN_LEFT_PERCENTAGE)}
          maxWidth={containerWidth * 0.7}
          enable={{ right: true }}
          onResizeStop={handleResizeStop}
          handleComponent={{
            right: <div className="w-1 h-full hover:bg-stone-300 cursor-ew-resize absolute right-0 top-0 z-10 transition-colors duration-200" />
          }}
          handleStyles={{
            right: {
              right: '-1px'
            }
          }}
        >
          <div className="flex flex-col h-full px-4 overflow-y-auto">
            {RenderTaskDetails()}
          </div>
        </Resizable>
      )}

      <div className={`flex flex-col w-full ${getIsMobileOnly() ? 'px-4' : 'pr-4'} ${(getIsMobileOnly()) ? !isTaskDetailsOpen ? 'h-3/4' : 'h-2/5' : 'h-full'}`}> 
        <div className={`flex flex-col w-full h-full bg-white rounded-xl my-3 mx-0 px-4 pt-2 shadow-xl border border-slate-200 transition-all duration-500 overflow-y-auto`}>
          <div className="flex justify-between py-0 px-2 border-b border-slate-200 pb-2">
            <button
              onClick={() => handleExitTask()}
              data-amp-track-section="Task Workspace"
              data-amp-track-action="Back_Click"
              data-amp-track-description="Back to Previous Page"
              className="flex items-center w-full gap-2 p-0 m-0 text-stone-600 hover:text-stone-800 bg-transparent hover:bg-transparent"
            >
              <ArrowLeft size={26} />
              {currentTask?.metaData?.projectId ? (
                <p className="flex leading-tight text-md" title={currentTask?.metaData?.projectTitle}>
                  {currentTask?.metaData?.projectTitle}
                </p>
              ) : (
                <div className="flex flex-row gap-2 text-left text-md w-full">
                  <DoorOpen size={22} /> 
                  <p className="flex leading-tight">Back</p>
                </div>
              )}
            </button>
          </div>

          {(!currentTask?.archived && !(currentTask?.metaData?.projectId && currentProject?.step !== 'in_progress')) && (
            <div className="flex gap-2 py-4 border-b border-stone-200">
              {getTaskControls()}
            </div>
          )}

          {/* Task Details */}
          <div className="flex justify-between py-2 px-4 border-b border-stone-200">
            <div className="flex gap-6">
              <div 
                className="flex flex-row gap-2 cursor-pointer"
                onClick={() => setIsRescheduling(true)}
                data-amp-track-section="Task Workspace"
                data-amp-track-action="Reschedule_Click"
                data-amp-track-description="Open Task Reschedule Modal"
              >
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="h-4 w-4" />
                  <span className="text-sm">
                    Start: {formatDate(currentTask?.startBy)}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="h-4 w-4" />
                  <span className="text-sm">
                    End: {formatDate(currentTask?.endBy)}
                  </span>
                </div>
              </div>
              {isRescheduling && (
                <TaskScheduler
                  task={currentTask}
                  onClose={() => setIsRescheduling(false)}
                  onSave={handleReschedule}
                />
              )}

              <div className="flex items-center gap-2 text-gray-600">
                <Clock className="h-4 w-4" />
                <span className="text-sm">
                  Estimation: {formatEstimation(currentTask?.estimation)}
                </span>
              </div>
            </div>
          </div>

          <div className="my-2 text-sm text-gray-600 clip">
            {currentTask?.description}
          </div>

          <div className="text-sm text-gray-600">
            Created On: {formatTaskDate(currentTask?.created_at, true)}
          </div>


          {currentTask?.actions?.length > 0 && (
            <div className="pt-2 px-4">
              <LatestActionSummary />
            </div>
          )}

          <div className="pt-2 px-4">{getTaskFiles()}</div>

          {currentTask?.notes && (
            <div className="pt-2 px-4 mb-4">
              <div className="flex items-center cursor-pointer mb-2 gap-2"
                onClick={() => setIsTaskNotesOpen(!isTaskNotesOpen)}
                data-amp-track-section="Task Workspace"
                data-amp-track-action="Instructions_Click"
                data-amp-track-description="Open Task Instructions"
              >
                <h3 className="text-sm font-medium text-gray-700">Instructions</h3>
                <div className="p-1 rounded-full bg-stone-200 hover:bg-stone-300">
                  <BookOpen className="h-4 w-4 text-gray-600" />
                </div>
              </div>
              {isTaskNotesOpen && (
                <div className="p-3 rounded-lg border border-stone-200 bg-stone-50">
                  <div className="text-sm text-gray-600 whitespace-pre-wrap">
                    {currentTask.notes}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
